/**
 * A3 v2 content brief — detail page.
 *
 * Tabs: Angle · Outline · FAQs · Titles/Meta/CTAs · Gaps · Advanced
 */
import { useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { motion } from 'framer-motion';
import {
  AlertOctagon,
  AlertTriangle,
  ArrowUpRight,
  BookOpen,
  Check,
  ClipboardList,
  Copy,
  Download,
  ExternalLink,
  FileText,
  Hash,
  Layers,
  Link as LinkIcon,
  ListTree,
  Megaphone,
  Loader2,
  RefreshCw,
  Sparkles,
  Tag,
  Target,
  Users,
} from 'lucide-react';
import type { Brief, BriefContent, BriefEvidenceCitation } from '@seo/types';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Spinner } from '@/components/spinner';
import { DetailError } from '@/components/detail-page';
import { StatusPill } from '@/components/status-pill';
import { EmptyState } from '@/components/empty-state';
import { OutlineTree } from '@/components/outline-tree';
import { JsonPre } from '@/components/json-pre';
import { PageHeader } from '@/components/page-header';
import { StatTile } from '@/components/ui/stat-tile';
import { Surface } from '@/components/ui/surface';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast, toastError } from '@/components/ui/toast';
import { useBrief, useBriefInsights } from '@/features/briefs/use-brief';
import { WritingPlanPanel } from '@/features/briefs/components/writing-plan-panel';
import { useRegenerateSection } from '@/features/briefs/use-regenerate-section';
import { useRegenerateDraft } from '@/features/briefs/use-regenerate-draft';
import { useRefreshStale } from '@/features/briefs/use-refresh-stale';
import { useProject } from '@/features/projects/use-project';
import { downloadAuthed } from '@/lib/download';
import { useAuthStore } from '@/lib/auth';
import { fadeUp, stagger } from '@/components/motion';
import { cn } from '@/lib/cn';

interface CtaVariant {
  headline: string;
  body: string;
  action_label: string;
  placement?: 'intro' | 'mid' | 'end';
}

function isCtaVariant(v: unknown): v is CtaVariant {
  return (
    !!v &&
    typeof v === 'object' &&
    typeof (v as CtaVariant).headline === 'string' &&
    typeof (v as CtaVariant).body === 'string' &&
    typeof (v as CtaVariant).action_label === 'string'
  );
}

export function BriefDetailRoute(): JSX.Element {
  const { briefId } = useParams({ strict: false }) as { briefId: string };
  const brief = useBrief(briefId);
  // Prime the project cache for breadcrumb resolution.
  useProject(brief.data?.project_id);
  const accessToken = useAuthStore((s) => s.accessToken);
  const tenant = useAuthStore((s) => s.tenant);
  const { toast } = useToast();
  const refreshStale = useRefreshStale(briefId);
  const [exporting, setExporting] = useState(false);

  if (brief.error) {
    return (
      <div className="space-y-4">
        <DetailError
          error={brief.error}
          entityKind="brief"
          backTo="/briefs"
          backLabel="Briefs"
        />
      </div>
    );
  }
  if (brief.isLoading || !brief.data) {
    return (
      <div className="flex justify-center p-10">
        <Spinner />
      </div>
    );
  }

  const b = brief.data;
  const content = b.brief_content ?? null;
  const staleSections = b.stale_sections ?? [];
  // Only offer refresh when the worker can actually regenerate (v2 + completed).
  const canRefreshStale =
    !!content && b.status === 'completed' && staleSections.length > 0;

  const exportPdf = async (): Promise<void> => {
    if (b.status !== 'completed' || exporting) return;
    setExporting(true);
    try {
      const safe = b.target_keyword
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '')
        .slice(0, 40);
      await downloadAuthed({
        url: `/v1/briefs/${b.id}/brief.pdf`,
        filename: `content-brief-${safe || b.id.slice(0, 8)}.pdf`,
        accessToken,
        tenantId: tenant?.id,
      });
      toast({ title: 'PDF downloaded' });
    } catch (e) {
      toastError(toast, e);
    } finally {
      setExporting(false);
    }
  };

  const refreshAllStale = (): void => {
    void (async () => {
      try {
        await refreshStale.mutateAsync();
        toast({
          title: 'Refreshing outdated sections',
          description: 'We will update the brief with the latest competitor research.',
        });
      } catch (e) {
        toastError(toast, e);
      }
    })();
  };

  return (
    <motion.div
      initial="hidden"
      animate="show"
      variants={stagger}
      className="space-y-6"
    >
      <PageHeader
        title={b.target_keyword}
        description={
          <>
            {b.status === 'completed'
              ? 'Writing plan ready — export a clear PDF for your team.'
              : `Article plan · ${b.status}`}
            {b.input_pins?.keyword_run_id ? (
              <>
                {' · '}
                <Link
                  to="/keywords/$keywordRunId"
                  params={{ keywordRunId: b.input_pins.keyword_run_id }}
                  className="font-medium text-foreground underline underline-offset-2 hover:text-primary"
                >
                  View source keyword run
                </Link>
              </>
            ) : null}
          </>
        }
        actions={
          <>
            <StatusPill status={b.status} />
            {canRefreshStale ? (
              <Button
                variant="outline"
                onClick={refreshAllStale}
                disabled={refreshStale.isPending}
              >
                <RefreshCw
                  className={cn(
                    'h-4 w-4',
                    refreshStale.isPending && 'animate-spin',
                  )}
                />
                Refresh outdated sections ({staleSections.length})
              </Button>
            ) : null}
            {b.status === 'completed' ? (
              <Button
                size="sm"
                disabled={exporting}
                onClick={() => void exportPdf()}
              >
                {exporting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
                {exporting ? 'Building PDF…' : 'Export PDF'}
              </Button>
            ) : null}
          </>
        }
      />

      {/* Status card — what's happening + what to do next */}
      <BriefStatusCard brief={b} />

      {/* Hero keyword card — same for v1 and v2 */}
      <motion.section
        variants={fadeUp}
        className="relative overflow-hidden rounded-2xl border border-border/60 bg-gradient-to-br from-primary/15 via-card/60 to-card/30 p-5 backdrop-blur"
      >
        <span
          aria-hidden
          className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-primary/30 opacity-50 blur-3xl"
        />
        <div className="relative">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-muted-foreground">
            <Sparkles className="h-3 w-3" />
            Target keyword
          </div>
          <div className="mt-1 text-xl font-semibold text-foreground">
            {b.target_keyword}
          </div>
          {b.secondary_keywords.length > 0 ? (
            <div className="mt-3">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                Secondary keywords
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {b.secondary_keywords.map((s) => (
                  <span
                    key={s}
                    className="inline-flex items-center gap-1 rounded-full border border-border/60 bg-background/60 px-2.5 py-1 text-xs text-foreground/90"
                  >
                    <Hash className="h-3 w-3 text-muted-foreground" />
                    {s}
                  </span>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      </motion.section>

      {/* v1 fallback: no v2 content payload → render legacy layout */}
      {!content ? (
        <V1BriefBody brief={b} />
      ) : (
        <V2BriefTabs brief={b} content={content} />
      )}
    </motion.div>
  );
}

/* ────────────────────── v1 fallback body ────────────────────── */

function V1BriefBody({ brief: b }: { brief: Brief }): JSX.Element {
  const insights = useBriefInsights(b.id, b.status === 'completed');
  const ctaVariants = Array.isArray(b.cta_variants)
    ? (b.cta_variants as unknown[]).filter(isCtaVariant)
    : [];
  const hasInternalLinks = (b.internal_links?.length ?? 0) > 0;

  return (
    <>
      {b.status === 'completed' ? (
        <motion.section variants={fadeUp} className="space-y-4">
          <WritingPlanPanel
            data={insights.data}
            loading={insights.isLoading}
            error={insights.error instanceof Error ? insights.error : null}
          />
        </motion.section>
      ) : null}

      <motion.section variants={fadeUp} className="space-y-3">
        <SectionHeading
          icon={Layers}
          title="Article outline"
          description="The H2/H3 structure to follow when you write."
        />
        {b.outline ? (
          <OutlineTree outline={b.outline} />
        ) : (
          <EmptyState
            title="Writing your outline"
            description="The outline will appear as soon as we finish drafting."
          />
        )}
      </motion.section>

      <motion.section variants={fadeUp} className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <MetaTitlesCard variants={b.meta_title_variants} />
        <MetaDescriptionsCard variants={b.meta_desc_variants} />
      </motion.section>

      {ctaVariants.length > 0 ? (
        <motion.section variants={fadeUp} className="space-y-3">
          <SectionHeading
            icon={Megaphone}
            title="Buttons for your reader"
            description="Copy one of these into your article where you want the reader to take the next step (usually near the end). Headline = title, body = short supporting line, button = the click text."
          />
          <CtaVariantsGrid variants={ctaVariants} />
        </motion.section>
      ) : null}

      {hasInternalLinks ? (
        <motion.section variants={fadeUp} className="space-y-3">
          <SectionHeading
            icon={LinkIcon}
            title="Link to these pages on your site"
            description="Add these links inside the article so readers (and Google) can find related pages on your domain."
          />
          <InternalLinksTable links={b.internal_links} />
        </motion.section>
      ) : null}
    </>
  );
}

/* ────────────────────── v2 tabbed body ────────────────────── */

const BRIEF_TABS = [
  'plan',
  'angle',
  'outline',
  'faqs',
  'meta',
  'gaps',
  'advanced',
] as const;
type BriefTab = (typeof BRIEF_TABS)[number];

function V2BriefTabs({
  brief: b,
  content,
}: {
  brief: Brief;
  content: BriefContent;
}): JSX.Element {
  const staleSet = useMemo(() => new Set(b.stale_sections ?? []), [b.stale_sections]);
  const evidenceIndex: Record<string, BriefEvidenceCitation> = useMemo(() => {
    if (b.evidence_index) return b.evidence_index;
    const out: Record<string, BriefEvidenceCitation> = {};
    for (const e of content.evidence_registry ?? []) out[e.id] = e;
    return out;
  }, [b.evidence_index, content.evidence_registry]);

  const insights = useBriefInsights(b.id, b.status === 'completed');

  // Active tab lives in the URL (?tab=gaps) — deep-linkable, shareable, and
  // Back/Forward step through tab changes. The default tab keeps a clean URL.
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { tab?: string };
  const tab: BriefTab = (BRIEF_TABS as readonly string[]).includes(search.tab ?? '')
    ? (search.tab as BriefTab)
    : 'plan';
  const onTabChange = (v: string): void => {
    void navigate({
      search: (prev) =>
        ({
          ...(prev as object),
          tab: v === 'plan' ? undefined : v,
        }) as never,
    });
  };

  return (
    <Tabs value={tab} onValueChange={onTabChange} className="w-full">
      <TabsList className="w-full justify-start overflow-x-auto">
        <TabsTrigger value="plan">Writing Plan</TabsTrigger>
        <TabsTrigger value="angle">Angle</TabsTrigger>
        <TabsTrigger value="outline">Outline</TabsTrigger>
        <TabsTrigger value="faqs">FAQs</TabsTrigger>
        <TabsTrigger value="meta">Titles & buttons</TabsTrigger>
        <TabsTrigger value="gaps">Gaps</TabsTrigger>
        <TabsTrigger value="advanced">Advanced</TabsTrigger>
      </TabsList>

      <TabsContent value="plan" className="space-y-4">
        <WritingPlanPanel
          data={insights.data}
          loading={insights.isLoading}
          error={insights.error instanceof Error ? insights.error : null}
        />
      </TabsContent>
      <TabsContent value="angle" className="space-y-4">
        <AngleTab brief={b} content={content} />
      </TabsContent>
      <TabsContent value="outline" className="space-y-4">
        <OutlineTab
          brief={b}
          content={content}
          evidenceIndex={evidenceIndex}
          staleSet={staleSet}
        />
      </TabsContent>
      <TabsContent value="faqs" className="space-y-4">
        <FaqsTab content={content} />
      </TabsContent>
      <TabsContent value="meta" className="space-y-4">
        <MetaTab content={content} />
      </TabsContent>
      <TabsContent value="gaps" className="space-y-4">
        <GapsTab content={content} />
      </TabsContent>
      <TabsContent value="advanced" className="space-y-6">
        <AdvancedTab brief={b} content={content} />
      </TabsContent>
    </Tabs>
  );
}

/* ────────────────────── Angle ────────────────────── */

function AngleTab({
  brief: b,
  content,
}: {
  brief: Brief;
  content: BriefContent;
}): JSX.Element {
  const recommendedAngle =
    content.recommended_angle ?? content.strategy.differentiator ?? '';
  const wc = content.structure_targets.target_word_count;
  const canRefreshStale =
    b.status === 'completed' && (b.stale_sections?.length ?? 0) > 0;

  return (
    <>
      {canRefreshStale ? (
        <div className="flex items-start gap-3 rounded-md border border-amber-500/40 bg-amber-500/5 p-4">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
          <div className="text-sm">
            <div className="font-semibold text-amber-700 dark:text-amber-400">
              {b.stale_sections.length} section
              {b.stale_sections.length === 1 ? '' : 's'} out of date
            </div>
            <div className="mt-1 text-muted-foreground">
              The search results and competitor pages this brief was based on have changed.
              Click "Refresh outdated sections" above and we'll rewrite them.
            </div>
          </div>
        </div>
      ) : null}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile
          label="How long to write"
          value={`${wc.min.toLocaleString()}–${wc.max.toLocaleString()} words`}
          icon={FileText}
        />
        <StatTile
          label="What readers want"
          value={
            <span className="capitalize">{content.intent_analysis.primary}</span>
          }
          hint={content.intent_analysis.secondary ?? undefined}
          icon={Target}
        />
        <StatTile
          label="Buyer stage"
          value={
            <span className="capitalize">
              {content.intent_analysis.funnel_stage}
            </span>
          }
          icon={Users}
        />
        <StatTile
          label="Reading time"
          value={`${content.structure_targets.target_reading_time_minutes} min`}
          icon={BookOpen}
        />
      </div>

      {recommendedAngle ? (
        <Surface tone="muted" className="border-l-4 border-l-primary p-4">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-muted-foreground">
            <Target className="h-3 w-3" />
            Your angle
          </div>
          <p className="mt-2 whitespace-pre-line text-[15px] leading-relaxed">
            {recommendedAngle}
          </p>
        </Surface>
      ) : (
        <EmptyState
          title="No angle yet"
          description="The recommended angle appears once drafting finishes."
        />
      )}

      {content.executive_summary ? (
        <Surface tone="raised" className="p-5">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-muted-foreground">
            <Sparkles className="h-3 w-3" />
            The plan in one paragraph
          </div>
          <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-foreground">
            {content.executive_summary}
          </p>
        </Surface>
      ) : null}
    </>
  );
}

/* ────────────────────── Strategy ────────────────────── */

function StrategyTab({ content }: { content: BriefContent }): JSX.Element {
  const ia = content.intent_analysis;
  const persona = content.target_persona;
  const strategy = content.strategy;
  const eeat = content.eeat_requirements;
  const schema = content.schema_recommendations;

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card
        icon={Target}
        title="What readers actually want"
        description="The main reason people search this — and why it matters for your article."
      >
        <dl className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <dt className="text-[10px] uppercase tracking-wider text-muted-foreground">
              Primary
            </dt>
            <dd className="mt-1 font-medium capitalize">{ia.primary}</dd>
          </div>
          {ia.secondary ? (
            <div>
              <dt className="text-[10px] uppercase tracking-wider text-muted-foreground">
                Secondary
              </dt>
              <dd className="mt-1 font-medium capitalize">{ia.secondary}</dd>
            </div>
          ) : null}
          <div>
            <dt className="text-[10px] uppercase tracking-wider text-muted-foreground">
              Buyer stage
            </dt>
            <dd className="mt-1 font-medium capitalize">{ia.funnel_stage}</dd>
          </div>
        </dl>
        <p className="mt-3 border-t border-border/50 pt-3 text-sm leading-relaxed text-muted-foreground">
          {ia.reasoning}
        </p>
      </Card>

      <Card
        icon={Users}
        title="Who you're writing for"
        description={`${persona.name} · ${persona.role}`}
      >
        <div className="space-y-3 text-sm">
          <BulletBlock label="What frustrates them" items={persona.pain_points} />
          <BulletBlock label="What they're trying to do" items={persona.goals} />
          {persona.objections && persona.objections.length > 0 ? (
            <BulletBlock label="What holds them back" items={persona.objections} />
          ) : null}
        </div>
      </Card>

      <Card icon={Sparkles} title="How to win this article">
        <dl className="space-y-3 text-sm">
          <Field label="Goal">{strategy.goal}</Field>
          <Field label="Format">{strategy.format}</Field>
          <Field label="What makes yours different">{strategy.differentiator}</Field>
          <Field label="Why readers will pick you">{strategy.unique_value_prop}</Field>
        </dl>
      </Card>

      <Card icon={ClipboardList} title="Trust and expertise checklist">
        <dl className="space-y-3 text-sm">
          <Field label="Show author byline">
            {eeat.byline_required ? 'Yes' : 'No'}
          </Field>
          <Field label="Author credentials to highlight">{eeat.author_credentials}</Field>
          <Field label="First-person voice">
            {eeat.first_person_expected ? 'Use it' : 'Avoid it'}
          </Field>
          <Field label="Sources to cite">
            {eeat.citations_expected.toString()}
          </Field>
          {eeat.review_by ? (
            <Field label="Have this reviewed by">{eeat.review_by}</Field>
          ) : null}
        </dl>
        {content.eeat_notes ? (
          <p className="mt-3 border-t border-border/50 pt-3 text-sm leading-relaxed text-muted-foreground">
            {content.eeat_notes}
          </p>
        ) : null}
      </Card>

      <Card icon={Tag} title="Structured data to add">
        <dl className="space-y-3 text-sm">
          <Field label="Main schema type">
            <span className="font-mono">{schema.primary_schema}</span>
          </Field>
          {schema.additional_schemas.length > 0 ? (
            <div>
              <dt className="text-[10px] uppercase tracking-wider text-muted-foreground">
                Extra schemas to include
              </dt>
              <dd className="mt-1 flex flex-wrap gap-1.5">
                {schema.additional_schemas.map((s) => (
                  <span
                    key={s}
                    className="rounded-full bg-muted px-2 py-0.5 font-mono text-[11px]"
                  >
                    {s}
                  </span>
                ))}
              </dd>
            </div>
          ) : null}
          {schema.notes ? <Field label="Notes">{schema.notes}</Field> : null}
        </dl>
      </Card>
    </div>
  );
}

/* ────────────────────── Outline ────────────────────── */

function OutlineTab({
  brief: b,
  content,
  evidenceIndex,
  staleSet,
}: {
  brief: Brief;
  content: BriefContent;
  evidenceIndex: Record<string, BriefEvidenceCitation>;
  staleSet: Set<string>;
}): JSX.Element {
  const sectionVersions = b.section_versions ?? {};
  const regenerate = useRegenerateSection(b.id);
  const { toast } = useToast();

  const refresh = async (sectionKey: string): Promise<void> => {
    try {
      await regenerate.mutateAsync({ section_key: sectionKey });
      toast({
        title: 'Rewriting this section',
        description: `The "${sectionKey}" section will refresh in a moment.`,
      });
    } catch (e) {
      toastError(toast, e);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3 rounded-xl border border-border/60 bg-gradient-to-br from-primary/10 via-card/50 to-card/30 p-4 backdrop-blur">
        <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary/15 ring-1 ring-primary/30">
          <ListTree className="h-4 w-4 text-primary" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
            H1 / Page title
          </div>
          <h2 className="text-lg font-semibold leading-tight text-foreground">
            {content.outline.h1}
          </h2>
        </div>
      </div>

      <ol className="space-y-3">
        {content.outline.sections.map((section, idx) => {
          const key = sectionKeyFor(section.h2, idx);
          const isStale = staleSet.has(key);
          const version = sectionVersions[key];
          return (
            <li
              key={key}
              className={cn(
                'rounded-xl border border-border/70 bg-card p-4',
                isStale && 'border-l-4 border-l-amber-500',
              )}
            >
              <div className="flex items-start gap-3">
                <span className="tabular mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-background/70 font-mono text-xs text-muted-foreground ring-1 ring-border/60">
                  {String(idx + 1).padStart(2, '0')}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-sm font-semibold text-foreground">
                      {section.h2}
                    </h3>
                    {isStale ? (
                      <span className="rounded-full border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-700 dark:text-amber-400">
                        out of date
                      </span>
                    ) : null}
                    {version && version > 1 ? (
                      <span className="rounded-full border border-border/60 bg-muted px-2 py-0.5 font-mono text-[10px] text-muted-foreground">
                        v{version}
                      </span>
                    ) : null}
                  </div>

                  {section.talking_points.length > 0 ? (
                    <ul className="mt-2 space-y-1 pl-1 text-sm">
                      {section.talking_points.map((tp, i) => (
                        <li
                          key={i}
                          className="flex items-start gap-2 text-muted-foreground"
                        >
                          <span className="mt-0.5 text-primary">·</span>
                          <span>{tp}</span>
                        </li>
                      ))}
                    </ul>
                  ) : null}

                  {section.evidence_ids.length > 0 ? (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {section.evidence_ids.map((id) => (
                        <EvidenceChip key={id} citation={evidenceIndex[id]} id={id} />
                      ))}
                    </div>
                  ) : null}

                  {section.h3s.length > 0 ? (
                    <ul className="mt-3 space-y-1.5 border-t border-border/50 pt-3">
                      {section.h3s.map((h3, i) => (
                        <li
                          key={`${h3}-${i}`}
                          className="flex items-center gap-2 rounded-md bg-background/40 px-3 py-2 text-sm text-foreground/90 ring-1 ring-border/40"
                        >
                          <span className="font-mono text-[10px] text-muted-foreground">
                            H3
                          </span>
                          <span className="truncate">{h3}</span>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
                {b.status === 'completed' ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => refresh(key)}
                    disabled={regenerate.isPending}
                    title="Rewrite this section using fresh data"
                  >
                    <RefreshCw
                      className={cn(
                        'h-3.5 w-3.5',
                        regenerate.isPending && 'animate-spin',
                      )}
                    />
                    Refresh
                  </Button>
                ) : null}
              </div>
            </li>
          );
        })}
      </ol>

      {content.outline_reordering_suggestions &&
      content.outline_reordering_suggestions.length > 0 ? (
        <Surface tone="muted" className="p-4">
          <div className="mb-2 text-sm font-semibold">
            Ideas to reorder your sections
          </div>
          <ul className="space-y-2 text-sm">
            {content.outline_reordering_suggestions.map((s, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className="rounded-full border border-border/70 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
                  {s.suggested_order_change.replace(/_/g, ' ')}
                </span>
                <div className="flex-1">
                  <div className="font-medium">{s.current_h2}</div>
                  <div className="text-muted-foreground">{s.reasoning}</div>
                </div>
              </li>
            ))}
          </ul>
        </Surface>
      ) : null}
    </div>
  );
}

function sectionKeyFor(h2: string, idx: number): string {
  const slug = h2
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
  return slug || `section-${idx + 1}`;
}

function EvidenceChip({
  citation,
  id,
}: {
  citation: BriefEvidenceCitation | undefined;
  id: string;
}): JSX.Element {
  if (!citation) {
    return (
      <span className="rounded-full bg-muted px-2 py-0.5 font-mono text-[10px] text-muted-foreground">
        {id}
      </span>
    );
  }
  const shortRef =
    citation.ref.length > 40 ? `${citation.ref.slice(0, 37)}…` : citation.ref;
  return (
    <span
      title={citation.quote_snippet ?? citation.ref}
      className="inline-flex items-center gap-1 rounded-full border border-border/60 bg-background/60 px-2 py-0.5 text-[10px] text-foreground/90"
    >
      <span className="rounded-full bg-muted px-1 py-0.5 font-mono text-[9px] uppercase tracking-wide text-muted-foreground">
        {citation.source_type.replace('_', ' ')}
      </span>
      <span className="max-w-[240px] truncate">{shortRef}</span>
    </span>
  );
}

/* ────────────────────── FAQs ────────────────────── */

function FaqsTab({ content }: { content: BriefContent }): JSX.Element {
  if (content.faqs.length === 0) {
    return (
      <EmptyState
        title="No FAQs yet"
        description="FAQ suggestions appear once drafting finishes."
      />
    );
  }
  return (
    <Card
      icon={ClipboardList}
      title="Questions to answer"
      description={`${content.faqs.length} FAQ${content.faqs.length === 1 ? '' : 's'} — each includes a paste-ready schema snippet for Google.`}
    >
      <ol className="space-y-3">
        {content.faqs.map((f, i) => (
          <FaqItem key={i} question={f.question} answer={f.answer_outline} />
        ))}
      </ol>
      <div className="mt-4 border-t border-border/50 pt-3">
        <CopyButton
          label="Copy FAQ code for Google"
          payload={buildFaqSchema(content.faqs)}
        />
      </div>
    </Card>
  );
}

/* ────────────────────── Gaps ────────────────────── */

function GapsTab({ content }: { content: BriefContent }): JSX.Element {
  if (!content.gaps_to_fill || content.gaps_to_fill.length === 0) {
    return (
      <EmptyState
        title="No content gaps flagged"
        description="Gaps appear when a competitor analysis is pinned to this brief."
      />
    );
  }
  return (
    <Card icon={AlertTriangle} title="What competitors missed — cover it and win">
      <ul className="space-y-2 text-sm">
        {content.gaps_to_fill.map((g, i) => (
          <li key={i} className="rounded-md border border-border/60 bg-card p-2">
            <div className="flex items-baseline gap-2">
              <span className="rounded-full border border-border/70 px-2 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
                {g.gap_type}
              </span>
              <span className="font-medium">{g.title}</span>
            </div>
            <div className="mt-1 text-muted-foreground">{g.why_matters}</div>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function FaqItem({
  question,
  answer,
}: {
  question: string;
  answer: string;
}): JSX.Element {
  return (
    <li className="rounded-md border border-border/60 bg-background/40 p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="text-sm font-semibold text-foreground">{question}</div>
        <CopyButton
          label=""
          payload={buildFaqSchema([{ question, answer_outline: answer }])}
          compact
        />
      </div>
      <p className="mt-1.5 whitespace-pre-line text-sm leading-relaxed text-muted-foreground">
        {answer}
      </p>
    </li>
  );
}

function buildFaqSchema(
  faqs: Array<{ question: string; answer_outline: string }>,
): string {
  const payload = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: f.answer_outline,
      },
    })),
  };
  return JSON.stringify(payload, null, 2);
}

/* ────────────────────── Meta tab ────────────────────── */

function MetaTab({ content }: { content: BriefContent }): JSX.Element {
  const titles = content.meta_title_variants ?? [];
  const descs = content.meta_desc_variants ?? [];
  const slugs = content.url_slugs ?? [];
  const ctas = content.cta_variants ?? [];

  type Placement =
    | 'intro'
    | 'mid'
    | 'end'
    | 'inline_lead_magnet'
    | 'trust_badge_row'
    | 'pricing_block'
    | 'demo_block'
    | 'exit_intent';
  const ctaByPlacement: Record<Placement, typeof ctas> = {
    intro: [],
    mid: [],
    end: [],
    inline_lead_magnet: [],
    trust_badge_row: [],
    pricing_block: [],
    demo_block: [],
    exit_intent: [],
  };
  for (const c of ctas) ctaByPlacement[c.placement]?.push(c);
  const placementLabel: Record<Placement, string> = {
    intro: 'Near the start',
    mid: 'In the middle',
    end: 'Near the end',
    inline_lead_magnet: 'Lead magnet inline',
    trust_badge_row: 'Trust badges',
    pricing_block: 'Pricing block',
    demo_block: 'Demo block',
    exit_intent: 'On exit intent',
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Google title and snippet options, plus ready-to-paste buttons for your article.
      </p>

      <Card icon={FileText} title="Page title options (for Google)">
        <p className="mb-3 text-xs text-muted-foreground">
          Pick one for the browser tab / Google result title.
        </p>
        <ol className="space-y-2">
          {titles.map((t, i) => (
            <LengthBarItem
              key={i}
              index={i}
              text={t}
              greenMax={60}
              amberMax={70}
            />
          ))}
        </ol>
      </Card>

      <Card icon={FileText} title="Search snippet options">
        <p className="mb-3 text-xs text-muted-foreground">
          The short description under your title in Google.
        </p>
        <ol className="space-y-2">
          {descs.map((d, i) => (
            <LengthBarItem
              key={i}
              index={i}
              text={d}
              greenMin={140}
              greenMax={160}
              amberMax={175}
              multiline
            />
          ))}
        </ol>
      </Card>

      {slugs.length > 0 ? (
        <Card icon={LinkIcon} title="URL options">
          <p className="mb-3 text-xs text-muted-foreground">
            Suggested path for this page (after your domain).
          </p>
          <ol className="space-y-2">
            {slugs.map((s, i) => (
              <li
                key={i}
                className="flex items-center gap-3 rounded-md border border-border/60 bg-background/40 p-2"
              >
                <span className="tabular flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/15 font-mono text-[11px] text-primary ring-1 ring-primary/30">
                  {i + 1}
                </span>
                <span className="min-w-0 flex-1 truncate font-mono text-sm text-foreground/90">
                  /{s}
                </span>
                <CopyButton label="" payload={s} compact />
              </li>
            ))}
          </ol>
        </Card>
      ) : null}

      {ctas.length > 0 ? (
        <Card icon={Megaphone} title="Buttons for your reader">
          <p className="mb-3 text-xs text-muted-foreground">
            Paste one into the article where you want the next step (intro / middle / end).
            Headline = title, body = short line, button = click text.
          </p>
          {(
            [
              'intro',
              'mid',
              'end',
              'inline_lead_magnet',
              'trust_badge_row',
              'pricing_block',
              'demo_block',
              'exit_intent',
            ] as const
          ).map((p) =>
            ctaByPlacement[p].length > 0 ? (
              <div key={p} className="mb-4 last:mb-0">
                <div className="mb-2 flex items-center gap-2">
                  <span className="rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-primary">
                    {placementLabel[p]}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {ctaByPlacement[p].length} option
                    {ctaByPlacement[p].length === 1 ? '' : 's'}
                  </span>
                </div>
                <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                  {ctaByPlacement[p].map((c, i) => (
                    <div
                      key={i}
                      className="rounded-md border border-border/60 bg-card p-3"
                    >
                      <div className="text-sm font-semibold">{c.headline}</div>
                      <p className="mt-1 text-xs text-muted-foreground">{c.body}</p>
                      <div className="mt-2 inline-flex items-center gap-1 rounded-md bg-primary/90 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-primary-foreground">
                        {c.action_label}
                        <ArrowUpRight className="h-3 w-3" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : null,
          )}
        </Card>
      ) : null}
    </div>
  );
}

function LengthBarItem({
  index,
  text,
  greenMin,
  greenMax,
  amberMax,
  multiline = false,
}: {
  index: number;
  text: string;
  greenMin?: number;
  greenMax: number;
  amberMax: number;
  multiline?: boolean;
}): JSX.Element {
  const len = text.length;
  const tone =
    len > amberMax
      ? 'text-destructive'
      : len > greenMax
        ? 'text-amber-600 dark:text-amber-400'
        : greenMin !== undefined && len < greenMin
          ? 'text-amber-600 dark:text-amber-400'
          : 'text-emerald-700 dark:text-emerald-400';
  return (
    <li className="rounded-md border border-border/60 bg-background/40 p-3">
      <div className="flex items-center justify-between gap-3">
        <span className="tabular flex h-6 w-6 items-center justify-center rounded-full bg-primary/15 font-mono text-[11px] text-primary ring-1 ring-primary/30">
          {index + 1}
        </span>
        <div className="flex items-center gap-2">
          <span className={cn('tabular font-mono text-[11px]', tone)}>
            {len} chars
          </span>
          <CopyButton label="" payload={text} compact />
        </div>
      </div>
      <p
        className={cn(
          'mt-2 text-sm text-foreground',
          multiline ? 'leading-relaxed' : 'truncate',
        )}
      >
        {text}
      </p>
    </li>
  );
}

/* ────────────────────── Distribution ────────────────────── */

function DistributionTab({
  brief: b,
  content,
}: {
  brief: Brief;
  content: BriefContent;
}): JSX.Element {
  const links = b.internal_links ?? [];
  const externals = content.external_sources ?? [];
  const checklist = content.publish_checklist ?? [];
  const byCategory: Record<
    'technical' | 'editorial' | 'distribution',
    typeof checklist
  > = { technical: [], editorial: [], distribution: [] };
  for (const c of checklist) byCategory[c.category]?.push(c);

  return (
    <div className="space-y-4">
      {links.length > 0 ? (
        <Card icon={LinkIcon} title="Link to these pages on your site">
          <p className="mb-3 text-xs text-muted-foreground">
            Add these links inside the article so readers can find related pages on your
            domain.
          </p>
          <InternalLinksTable links={links} />
        </Card>
      ) : null}

      <Card icon={ExternalLink} title="Outside sources to reference">
        {externals.length === 0 ? (
          <EmptyState
            title="No specific sources suggested"
            description="Your writer can pick the right URLs when drafting."
          />
        ) : (
          <ul className="space-y-2">
            {externals.map((s, i) => (
              <li
                key={i}
                className="rounded-md border border-border/60 bg-card p-3"
              >
                <div className="flex items-baseline gap-2">
                  <span className="flex-1 font-medium">{s.label}</span>
                  <span className="rounded-full bg-muted px-2 py-0.5 font-mono text-[10px] text-muted-foreground">
                    suggested
                  </span>
                </div>
                <div className="mt-1 text-xs text-muted-foreground">{s.url_hint}</div>
                <div className="mt-1 text-sm">{s.why}</div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card icon={ClipboardList} title="Before-you-publish checklist">
        {(['technical', 'editorial', 'distribution'] as const).map((cat) =>
          byCategory[cat].length > 0 ? (
            <div key={cat} className="mb-4 last:mb-0">
              <div className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                {cat}
              </div>
              <ul className="space-y-1 pl-1 text-sm">
                {byCategory[cat].map((c, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-2 text-muted-foreground"
                  >
                    <Check className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-emerald-600" />
                    <span>{c.item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null,
        )}
      </Card>
    </div>
  );
}

/* ────────────────────── Benchmark ────────────────────── */

function BenchmarkTab({ content }: { content: BriefContent }): JSX.Element {
  const rows = content.competitor_benchmark ?? [];
  if (rows.length === 0) {
    return (
      <EmptyState
        title="Comparing you to competitors..."
        description="This table appears once we've analyzed how each top-ranking page stacks up."
      />
    );
  }
  return (
    <div className="overflow-hidden rounded-xl border border-border/60 bg-card/40 backdrop-blur">
      <table className="w-full text-sm">
        <thead className="bg-background/40 text-[10px] uppercase tracking-wider text-muted-foreground">
          <tr>
            <th className="px-4 py-2 text-left font-medium">Page</th>
            <th className="px-4 py-2 text-right font-medium">Words</th>
            <th className="px-4 py-2 text-left font-medium">Their angle</th>
            <th className="px-4 py-2 text-left font-medium">What they do well</th>
            <th className="px-4 py-2 text-left font-medium">Where they miss</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-border/40 align-top">
              <td className="px-4 py-2 font-mono text-xs">
                <a
                  href={r.url}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="inline-flex items-center gap-1 text-foreground hover:underline"
                >
                  <span className="max-w-[280px] truncate">{r.url}</span>
                  <ExternalLink className="h-3 w-3 shrink-0 text-muted-foreground" />
                </a>
              </td>
              <td className="tabular px-4 py-2 text-right">
                {r.word_count.toLocaleString()}
              </td>
              <td className="px-4 py-2 text-foreground/90">{r.unique_angle}</td>
              <td className="px-4 py-2 text-emerald-700 dark:text-emerald-400">
                {r.strength}
              </td>
              <td className="px-4 py-2 text-amber-700 dark:text-amber-400">
                {r.weakness}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ────────────────────── Advanced (progressive disclosure) ────────────────────── */

function AdvancedTab({
  brief,
  content,
}: {
  brief: Brief;
  content: BriefContent;
}): JSX.Element {
  const instructions = content.ai_writer_instructions ?? '';
  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">
        Extra detail for power users — persona, schema, media, checklist, and developer
        data. Day-to-day writing lives in the Writing Plan tab.
      </p>
      <StrategyTab content={content} />
      <Card icon={Hash} title="Topics and terms to include">
        <div className="flex flex-wrap gap-1.5">
          {content.entities_to_cover.map((e) => (
            <span
              key={e}
              className="rounded-full bg-muted px-2.5 py-1 text-xs text-foreground/90"
            >
              {e}
            </span>
          ))}
        </div>
      </Card>

      <Card icon={FileText} title="Images and videos to add">
        {content.media_recommendations.length === 0 ? (
          <EmptyState
            title="No media suggestions yet"
            description="Image and video ideas appear once the drafting step is done."
          />
        ) : (
          <div className="overflow-hidden rounded-md border border-border/60">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-[10px] uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">Type</th>
                  <th className="px-3 py-2 text-left font-medium">Where in the article</th>
                  <th className="px-3 py-2 text-left font-medium">Why include it</th>
                  <th className="px-3 py-2 text-left font-medium">Alt text idea</th>
                </tr>
              </thead>
              <tbody>
                {content.media_recommendations.map((m, i) => (
                  <tr key={i} className="border-t border-border/50">
                    <td className="px-3 py-2 capitalize text-foreground">
                      {m.media_type}
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">
                      {m.placement}
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">
                      {m.purpose}
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">
                      {m.alt_text_hint}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card icon={AlertTriangle} title="Traps to avoid">
        {content.avoid.length === 0 ? (
          <EmptyState
            title="You're in the clear"
            description="Nothing to specifically watch out for on this article."
          />
        ) : (
          <ul className="space-y-1.5 pl-1 text-sm">
            {content.avoid.map((a, i) => (
              <li key={i} className="flex items-start gap-2 text-muted-foreground">
                <span className="mt-0.5 text-destructive">·</span>
                <span>{a}</span>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {content.top_risks && content.top_risks.length > 0 ? (
        <Card icon={AlertOctagon} title="Top risks">
          <ul className="space-y-1.5 pl-1 text-sm">
            {content.top_risks.map((r, i) => (
              <li key={i} className="flex items-start gap-2 text-muted-foreground">
                <span className="mt-0.5 text-destructive">·</span>
                <span>{r}</span>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}

      <DistributionTab brief={brief} content={content} />
      <BenchmarkTab content={content} />
      {instructions ? (
        <Card
          icon={Copy}
          title="Copy prompt for your LLM"
          description="Paste into ChatGPT, Claude, or another writing tool. We no longer draft full articles in-app."
        >
          <div className="mb-3 flex justify-end">
            <CopyButton label="Copy the AI prompt" payload={instructions} />
          </div>
          <pre className="max-h-[40vh] overflow-auto rounded-md border border-border/60 bg-muted/30 p-4 text-xs leading-relaxed text-foreground whitespace-pre-wrap">
            {instructions}
          </pre>
        </Card>
      ) : null}
      <JsonPre data={brief} label="For developers — show raw JSON" />
    </div>
  );
}

/* ────────────────────── shared bits ────────────────────── */

/**
 * Status card at the top of the detail page. Communicates:
 *   - Running: which phase, when it started, expected ETA
 *   - Completed with warnings: what's missing + how to retry
 *   - Failed: user-friendly reason + explicit "Try again" action
 *   - Completed clean: a compact success chip
 * The goal is that a non-technical user always knows what's happening.
 */
function BriefStatusCard({ brief }: { brief: Brief }): JSX.Element | null {
  const status = brief.status;
  const warnings = Array.isArray(brief.warnings) ? brief.warnings : [];
  const isRunning =
    status === 'queued' ||
    status === 'researching' ||
    status === 'drafting' ||
    status === 'delivering';
  const isFailed = status === 'failed' || status === 'cancelled';
  const isCompleted = status === 'completed';
  const startedAt = brief.created_at ? new Date(brief.created_at) : null;
  const failedAt = brief.failed_at ? new Date(brief.failed_at) : null;

  // Show the "Re-run writer step" button when the last writer run degraded.
  // Available on completed briefs (writer fell back to a deterministic draft)
  // and on failed briefs whose failure was a writer degradation.
  const regenerateDraft = useRegenerateDraft(brief.id);
  const { toast } = useToast();
  const canRegenerateDraft = warnings.some(
    (w) =>
      typeof (w as { code?: unknown }).code === 'string' &&
      ((w as { code: string }).code).startsWith('BRIEF_DRAFT_DEGRADED'),
  ) && (isCompleted || status === 'failed');

  const rerunWriter = (): void => {
    void (async () => {
      try {
        await regenerateDraft.mutateAsync();
        toast({
          title: 'Re-running the writer step',
          description:
            'We are re-drafting the qualitative sections. The plan is preserved, so this is much faster than a full rerun.',
        });
      } catch (e) {
        toastError(toast, e);
      }
    })();
  };

  const PHASE_COPY: Record<string, { title: string; body: string }> = {
    queued: {
      title: 'Waiting to start',
      body: "Your brief is in line — we'll pick it up in a moment.",
    },
    researching: {
      title: 'Gathering evidence',
      body: 'Pulling competitor analysis, keyword context, and site signals for this brief.',
    },
    drafting: {
      title: 'Writing your brief',
      body: 'Building the outline, FAQs, titles, meta, and CTAs.',
    },
    delivering: {
      title: 'Almost done',
      body: 'Saving your brief so you can export the PDF.',
    },
  };

  // Only render if there's something meaningful to say.
  const hasWarnings = warnings.length > 0;
  if (isCompleted && !hasWarnings) return null; // stay out of the way

  return (
    <motion.section variants={fadeUp}>
      {isRunning ? (
        <div className="flex items-start gap-3 rounded-xl border border-primary/30 bg-primary/5 p-4">
          <Loader2 className="mt-0.5 h-4 w-4 flex-shrink-0 animate-spin text-primary" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-foreground">
              {PHASE_COPY[status]?.title ?? 'Working on it'}
            </div>
            <div className="mt-0.5 text-xs text-muted-foreground">
              {PHASE_COPY[status]?.body ?? "We're generating your brief."}
              {startedAt ? (
                <> · Started {startedAt.toLocaleTimeString()}</>
              ) : null}
            </div>
            <div className="mt-1 text-[11px] text-muted-foreground/80">
              This usually takes 2-3 minutes. You can leave this tab open — it refreshes on its own.
            </div>
          </div>
        </div>
      ) : isFailed ? (
        <div className="flex items-start gap-3 rounded-xl border border-destructive/40 bg-destructive/5 p-4">
          <AlertOctagon className="mt-0.5 h-5 w-5 flex-shrink-0 text-destructive" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-destructive">
              {status === 'cancelled' ? 'Brief was stopped' : "We couldn't finish your brief"}
            </div>
            <div className="mt-1 text-sm text-foreground/90">
              {brief.failure_reason ||
                "Something went wrong while writing this brief. Try starting a new one."}
            </div>
            <div className="mt-2 text-[11px] text-muted-foreground">
              {failedAt ? <>Failed at {failedAt.toLocaleString()}.</> : null}
            </div>
            {canRegenerateDraft ? (
              <div className="mt-3">
                <Button
                  size="sm"
                  onClick={rerunWriter}
                  disabled={regenerateDraft.isPending}
                >
                  {regenerateDraft.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4" />
                  )}
                  {regenerateDraft.isPending ? 'Re-running…' : 'Re-run writer step'}
                </Button>
                <p className="mt-1 text-[11px] text-muted-foreground">
                  Skips the 10-min planning step — reuses the plan we already have.
                </p>
              </div>
            ) : null}
          </div>
        </div>
      ) : hasWarnings ? (
        <div className="flex items-start gap-3 rounded-xl border border-amber-500/30 bg-amber-500/5 p-4">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-amber-700 dark:text-amber-400">
              Your brief is ready — with a heads-up on {warnings.length} thing{warnings.length === 1 ? '' : 's'}
            </div>
            <ul className="mt-1 space-y-1 text-sm text-foreground/90">
              {warnings.slice(0, 3).map((w, i) => (
                <li key={i} className="flex items-start gap-1.5">
                  <span className="mt-1.5 h-1 w-1 flex-shrink-0 rounded-full bg-amber-500" />
                  <span>{w.message ?? 'A step in your brief reported a warning.'}</span>
                </li>
              ))}
            </ul>
            {canRegenerateDraft ? (
              <div className="mt-3">
                <Button
                  size="sm"
                  onClick={rerunWriter}
                  disabled={regenerateDraft.isPending}
                >
                  {regenerateDraft.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4" />
                  )}
                  {regenerateDraft.isPending ? 'Re-running…' : 'Re-run writer step'}
                </Button>
                <p className="mt-1 text-[11px] text-muted-foreground">
                  Reuses the plan (saves ~10 min); re-drafts the qualitative sections.
                </p>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </motion.section>
  );
}

function SectionHeading({
  icon: Icon,
  title,
  description,
}: {
  icon: typeof Layers;
  title: string;
  description?: string;
}): JSX.Element {
  return (
    <div className="flex items-start gap-3">
      <span className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-md bg-primary/10 ring-1 ring-primary/30">
        <Icon className="h-4 w-4 text-primary" />
      </span>
      <div>
        <h2 className="text-lg font-semibold text-foreground">{title}</h2>
        {description ? (
          <p className="text-sm text-muted-foreground">{description}</p>
        ) : null}
      </div>
    </div>
  );
}

function Card({
  icon: Icon,
  title,
  description,
  children,
}: {
  icon: typeof Layers;
  title: string;
  description?: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <Surface className="p-4">
      <div className="mb-3 flex items-start gap-3">
        <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 ring-1 ring-primary/30">
          <Icon className="h-4 w-4 text-primary" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-foreground">{title}</div>
          {description ? (
            <div className="text-xs text-muted-foreground">{description}</div>
          ) : null}
        </div>
      </div>
      {children}
    </Surface>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <div>
      <dt className="text-[10px] uppercase tracking-wider text-muted-foreground">
        {label}
      </dt>
      <dd className="mt-1 text-foreground/90">{children}</dd>
    </div>
  );
}

function BulletBlock({
  label,
  items,
}: {
  label: string;
  items: string[];
}): JSX.Element {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <ul className="mt-1 space-y-1 pl-1">
        {items.map((it, i) => (
          <li key={i} className="flex items-start gap-2 text-muted-foreground">
            <span className="mt-0.5 text-primary">·</span>
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function CopyButton({
  label,
  payload,
  compact = false,
}: {
  label: string;
  payload: string;
  compact?: boolean;
}): JSX.Element {
  const [copied, setCopied] = useState(false);
  const copy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(payload);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard not available — ignore */
    }
  };
  if (compact) {
    return (
      <button
        type="button"
        onClick={copy}
        aria-label="Copy"
        className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-border/60 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
      >
        {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
      </button>
    );
  }
  return (
    <Button variant="outline" onClick={copy}>
      {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
      {copied ? 'Copied' : label}
    </Button>
  );
}

/* ────────────────────── v1 legacy sub-components ────────────────────── */

function MetaTitlesCard({ variants }: { variants: string[] | null }): JSX.Element {
  return (
    <div className="rounded-2xl border border-border/60 bg-card/50 p-4 backdrop-blur">
      <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-foreground">
        <FileText className="h-4 w-4 text-primary" />
        Page title options (for Google)
      </div>
      {!variants || variants.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Title suggestions will show up once the brief is done drafting.
        </p>
      ) : (
        <ol className="space-y-2">
          {variants.map((title, i) => {
            const len = title.length;
            const tone =
              len > 60 ? 'text-amber-300' : len < 30 ? 'text-amber-300' : 'text-emerald-300';
            return (
              <li
                key={i}
                className="flex items-start gap-3 rounded-md border border-border/40 bg-background/40 p-3"
              >
                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/15 font-mono text-[11px] text-primary ring-1 ring-primary/30">
                  {i + 1}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-foreground">{title}</div>
                  <div className={cn('mt-1 font-mono text-[11px]', tone)}>
                    {len} chars
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}

function MetaDescriptionsCard({
  variants,
}: {
  variants: string[] | null;
}): JSX.Element {
  return (
    <div className="rounded-2xl border border-border/60 bg-card/50 p-4 backdrop-blur">
      <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-foreground">
        <FileText className="h-4 w-4 text-primary" />
        Search snippet options
      </div>
      {!variants || variants.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Snippet suggestions will show up once the brief is done drafting.
        </p>
      ) : (
        <ol className="space-y-2">
          {variants.map((desc, i) => {
            const len = desc.length;
            const tone =
              len > 160 ? 'text-amber-300' : len < 70 ? 'text-amber-300' : 'text-emerald-300';
            return (
              <li
                key={i}
                className="rounded-md border border-border/40 bg-background/40 p-3"
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/15 font-mono text-[11px] text-primary ring-1 ring-primary/30">
                    {i + 1}
                  </span>
                  <span className={cn('font-mono text-[11px]', tone)}>{len} chars</span>
                </div>
                <p className="mt-2 text-sm leading-relaxed text-foreground/90">{desc}</p>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}

function CtaVariantsGrid({ variants }: { variants: CtaVariant[] }): JSX.Element {
  if (variants.length === 0) {
    return (
      <EmptyState
        title="No button ideas yet"
        description="We’ll suggest buttons for readers once this brief finishes drafting."
      />
    );
  }
  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
      {variants.map((c, i) => (
        <div
          key={i}
          className="group relative overflow-hidden rounded-2xl border border-border/60 bg-card/50 p-5 backdrop-blur transition-shadow hover:shadow-glow-sm"
        >
          <span
            aria-hidden
            className="pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full bg-violet-500/20 opacity-60 blur-3xl"
          />
          <div className="relative">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-muted-foreground">
              <Megaphone className="h-3 w-3" />
              Option {i + 1}
            </div>
            <h3 className="mt-1 text-lg font-semibold text-foreground">{c.headline}</h3>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{c.body}</p>
            <div className="mt-4 inline-flex items-center gap-2 rounded-md bg-primary/90 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-primary-foreground">
              {c.action_label}
              <ArrowUpRight className="h-3 w-3" />
            </div>
            <p className="mt-3 text-[11px] text-muted-foreground">
              Use as: title → short line → button text
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}

function InternalLinksTable({
  links,
}: {
  links: Brief['internal_links'];
}): JSX.Element {
  if (!links || links.length === 0) {
    return (
      <EmptyState
        title="No site pages to link yet"
        description="This is optional. When we know more pages on your site, we’ll suggest which ones to link from this article."
      />
    );
  }
  return (
    <div className="overflow-hidden rounded-2xl border border-border/60 bg-card/40 backdrop-blur">
      <table className="w-full text-sm">
        <thead className="bg-background/40 text-[10px] uppercase tracking-wider text-muted-foreground">
          <tr>
            <th className="px-4 py-2 text-left font-medium">Page to link to</th>
            <th className="px-4 py-2 text-left font-medium">Link text</th>
            <th className="px-4 py-2 text-right font-medium">Fit</th>
          </tr>
        </thead>
        <tbody>
          {links.map((l, i) => (
            <tr key={`${l.url}-${i}`} className="border-t border-border/40">
              <td className="px-4 py-2 font-mono text-xs">
                <a
                  href={l.url}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="inline-flex items-center gap-1 text-foreground hover:underline"
                >
                  <span className="max-w-[360px] truncate">{l.url}</span>
                  <ExternalLink className="h-3 w-3 shrink-0 text-muted-foreground" />
                </a>
              </td>
              <td className="px-4 py-2 text-foreground/90">{l.anchor}</td>
              <td className="px-4 py-2 text-right">
                <Badge variant="info">{l.score.toFixed(2)}</Badge>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
